/**
 * Поручения руководителя по показателям дашборда объекта — зеркало DTO
 * `/api/v1/agents/progress/instructions*` и `/api/v1/instructions/*`.
 */

import type { BadgeTone } from './types';
import { fmtShare, type ObjectDashboard } from './dashboard';

/** Блок дашборда, по которому выдано поручение. */
export type InstructionTopic =
  'correspondence' | 'claims' | 'progress' | 'billing' | 'ksp' | 'other';

export type InstructionStatus = 'issued' | 'done' | 'cancelled';

/** Снимок показателя на момент выдачи (строки «подпись — значение»). */
export interface IndicatorSnapshot {
  title: string;
  lines: [string, string][];
  at: string;
}

export interface ProgressInstruction {
  id: string;
  number: string;
  project_id: string;
  project_name: string;
  topic: InstructionTopic | string;
  indicator: IndicatorSnapshot;
  text: string;
  assignee_user_id: string | null;
  assignee_employee_id: string | null;
  assignee_name: string;
  due_date: string | null;
  status: InstructionStatus | string;
  issued_by: string | null;
  issued_by_name: string | null;
  created_at: string;
  updated_at: string;
  done_at: string | null;
  done_by: string | null;
  done_note: string | null;
  vk_sent_at: string | null;
  /** Срок прошёл, а поручение ещё выдано (считает сервер на сегодня по Москве). */
  overdue: boolean;
}

/** Ответ на выдачу поручения: запись + куда ушло. */
export interface CreatedInstruction extends ProgressInstruction {
  delivery: { bell: boolean; vk: boolean };
}

/** Кандидат в исполнители. */
export interface InstructionRecipient {
  /** `u:<id>` — учётка, `e:<id>` — сотрудник справочника. */
  key: string;
  name: string;
  department: string | null;
  position: string | null;
  user_id: string | null;
  employee_id: string | null;
  has_vk: boolean;
  has_account: boolean;
}

export interface NewInstruction {
  project_id: string;
  topic: InstructionTopic;
  text: string;
  recipient: string;
  due_date: string | null;
}

export function instructionTopicTitle(topic: string): string {
  switch (topic) {
    case 'correspondence':
      return 'Переписка с Заказчиком';
    case 'claims':
      return 'Претензии';
    case 'progress':
      return 'Прогресс по объекту';
    case 'billing':
      return 'Выполнено и запроцентовано';
    case 'ksp':
      return 'План и факт по графику';
    default:
      return 'Прочие показатели';
  }
}

export function instructionStatusLabel(i: Pick<ProgressInstruction, 'status' | 'overdue'>): string {
  if (i.status === 'issued') return i.overdue ? 'просрочено' : 'выдано';
  if (i.status === 'done') return 'выполнено';
  if (i.status === 'cancelled') return 'отменено';
  return i.status;
}

export function instructionStatusTone(
  i: Pick<ProgressInstruction, 'status' | 'overdue'>,
): BadgeTone {
  if (i.status === 'issued') return i.overdue ? 'danger' : 'warning';
  if (i.status === 'done') return 'success';
  return 'neutral';
}

/** Сохранить скачанный файл под понятным именем. */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Подсказка текста поручения по цифрам блока — отправная точка, руководитель
 * её правит. Цифры показателя в документ сервер добавляет сам.
 */
export function suggestInstructionText(topic: InstructionTopic, d: ObjectDashboard): string {
  switch (topic) {
    case 'correspondence': {
      const c = d.correspondence;
      if (c.awaiting_answer > 0) {
        const late = c.answer_overdue > 0 ? `, из них срок ответа истёк — ${c.answer_overdue}` : '';
        return `Подготовить и направить ответы Заказчику на письма, ожидающие ответа (${c.awaiting_answer}${late}). Доложить об исполнении.`;
      }
      return 'Проверить переписку с Заказчиком по объекту и доложить о письмах, требующих ответа.';
    }
    case 'claims': {
      const c = d.claims;
      if (c.awaiting > 0) {
        const late = c.overdue > 0 ? `, из них срок ответа истёк — ${c.overdue}` : '';
        return `Подготовить ответы на претензии Заказчика без ответа (${c.awaiting}${late}), согласовать с юристом и направить Заказчику.`;
      }
      return 'Проконтролировать претензионную работу по объекту и доложить о новых претензиях Заказчика.';
    }
    case 'progress':
      return d.progress.available
        ? `Представить план выполнения оставшихся объёмов по объекту (выполнено позиций РД: ${fmtShare(d.progress.completion_pct)}) с причинами отставания и мерами.`
        : 'Загрузить срез КФО Заказчика по объекту в ОСНОВА.Инженер и привязать договор к объекту.';
    case 'billing':
      return d.billing.lines_with_nzp > 0
        ? `Запроцентовать выполненные объёмы (позиций с НЗП: ${d.billing.lines_with_nzp}) в ближайшем ПА и представить сверку «факт — ПА».`
        : 'Представить сверку выполненных и запроцентованных объёмов по объекту.';
    case 'ksp':
      return 'Подготовить календарно-сетевой график объекта для загрузки в ОСНОВА.КСП.';
    default:
      return '';
  }
}
